import React, {useMemo, useState} from 'react';
import {Avatar, Button, Card, Col, Empty, Input, message, Row, Space, Spin, Tag, Tooltip, Upload} from 'antd';
import type {UploadFile} from 'antd/lib';
import {
  ClockCircleOutlined,
  DownloadOutlined,
  MessageOutlined,
  PaperClipOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import PrimaryButton from '../../../../components/common/PrimaryButton';
import IssueSummaryCard from '../components/IssueSummaryCard';
import {useTranslation} from '../../../../hooks/useTranslation';
import {useDateFormat} from '../../../../hooks/useDateFormat';
import {useIssueDetail} from '../../../../hooks/issues/useIssues';
import {
  downloadMonitoringFile,
  MONITORING_DATE_FORMAT,
  MonitoringFile,
  useCreateMonitoring,
  useIssueActivity,
  useMonitoringAttachments,
} from '../../../../hooks/issues/useIssueMonitoring';
import {tokens} from '../../../../theme/tokens';
import {extractErrorMessage} from '../../../../utils/apiError';

dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);

interface Props {
  issueId?: string; // route param = issue UID
  activePanel: string;
}

// Lazily fetches + renders the S3 files for one monitoring entry. Only mounted
// when the entry has an attachment, so entries without files cost no request.
const EntryAttachments: React.FC<{issueUid?: string; entryUid?: string}> = ({issueUid, entryUid}) => {
  const {t} = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const {data: files = [], isLoading} = useMonitoringAttachments(issueUid, entryUid);

  const handleView = (file: MonitoringFile) => {
    if (file.presignedUrl) {
      window.open(file.presignedUrl, '_blank', 'noopener,noreferrer');
    } else {
      messageApi.error(t('issues.activity.messages.attachmentError'));
    }
  };

  const handleDownload = async (file: MonitoringFile) => {
    try {
      await downloadMonitoringFile(file);
    } catch {
      messageApi.error(t('issues.activity.messages.attachmentError'));
    }
  };

  if (isLoading) return <Spin size="small"/>;
  if (files.length === 0) return null;

  return (
    <Space size={tokens.spacing.x8} className="gt-activity-entry-tags" wrap>
      {contextHolder}
      {files.map((file) => (
        <Tag key={file.key} icon={<PaperClipOutlined/>} className="gt-activity-entry-tag">
          <Tooltip title={t('issues.activity.attachment.view')}>
            <a onClick={() => handleView(file)}>{file.name}</a>
          </Tooltip>
          <Tooltip title={t('issues.activity.attachment.download')}>
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined/>}
              aria-label={t('issues.activity.attachment.download')}
              onClick={() => handleDownload(file)}
              style={{marginInlineStart: tokens.spacing.x4, height: 'auto', padding: 0}}
            />
          </Tooltip>
        </Tag>
      ))}
    </Space>
  );
};

// Build avatar initials from a full name ("Jane Smith" -> "JS").
const initialsOf = (name?: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const IssueActivity: React.FC<Props> = ({issueId}) => {
  const {t} = useTranslation();
  const {dateTimeFormat} = useDateFormat();
  const [messageApi, contextHolder] = message.useMessage();

  // The route id is the UID; the monitoring API needs the NUMERIC issue id.
  const {data: issueData} = useIssueDetail(issueId);
  const numericId = issueData?.id as number | undefined;
  const issueUid = (issueData?.uid as string | undefined) ?? issueId;
  const currentStateId = issueData?.status?.id;

  const {data: entries = [], isLoading} = useIssueActivity(numericId);
  const createMonitoring = useCreateMonitoring();

  const [draft, setDraft] = useState('');
  const [pendingFiles, setPendingFiles] = useState<UploadFile[]>([]);

  const summary = useMemo(
    () => ({
      code: issueData?.codeIncident,
      statusLabel: issueData?.status?.description,
      statusColor: issueData?.status?.colorStateIssue,
      reportedBy: issueData?.reportingUser ?? issueData?.registerUser,
      reportedOn: issueData?.dateReport,
    }),
    [issueData],
  );

  const canPost = (draft.trim().length > 0 || pendingFiles.length > 0) && !!numericId;

  const handlePost = async () => {
    if (!canPost || !numericId) return;
    const stateIncident = currentStateId != null ? Number(currentStateId) : undefined;
    if (stateIncident == null || Number.isNaN(stateIncident)) {
      messageApi.error(t('issues.activity.messages.postError'));
      return;
    }

    try {
      await createMonitoring.mutateAsync({
        idIssue: numericId,
        stateIncident,
        issueUid: issueUid ?? '',
        descriptionTrack: draft.trim(),
        files: pendingFiles
          .map((f) => f.originFileObj as File | undefined)
          .filter((f): f is File => !!f),
      });
      messageApi.success(t('issues.activity.messages.postSuccess'));
      setDraft('');
      setPendingFiles([]);
    } catch (error) {
      messageApi.error(extractErrorMessage(error, t('issues.activity.messages.postError')));
    }
  };

  return (
    <div className="gt-issue-activity">
      {contextHolder}
      <Row gutter={[tokens.spacing.x24, tokens.spacing.x24]}>
        <Col xs={24} lg={16}>
          <Card className="gt-activity-card gt-activity-compose">
            <div className="gt-activity-section-title">
              {t('issues.activity.compose.title')}
            </div>
            <Input.TextArea
              rows={4}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('issues.activity.compose.placeholder')}
              className="gt-activity-textarea"
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  handlePost();
                }
              }}
            />
            <div className="gt-activity-compose-actions">
              <Upload
                fileList={pendingFiles}
                onChange={(info) => setPendingFiles(info.fileList)}
                beforeUpload={() => false /* keep file local until Post */}
                multiple
              >
                <PrimaryButton size="small" icon={<UploadOutlined/>}>
                  {t('issues.activity.compose.attach')}
                </PrimaryButton>
              </Upload>
              <PrimaryButton
                type="primary"
                icon={<MessageOutlined/>}
                loading={createMonitoring.isPending}
                disabled={!canPost}
                onClick={handlePost}
              >
                {t('issues.activity.compose.post')}
              </PrimaryButton>
            </div>
          </Card>

          <Card
            className="gt-activity-card gt-activity-timeline-card"
            title={
              <Space size={tokens.spacing.x8}>
                <ClockCircleOutlined style={{color: tokens.colors.textSecondary}}/>
                <span>{t('issues.activity.timeline.title')}</span>
              </Space>
            }
          >
            {isLoading ? (
              <div style={{textAlign: 'center', padding: tokens.spacing.x24}}>
                <Spin/>
              </div>
            ) : entries.length === 0 ? (
              <Empty image={null} description={t('issues.activity.timeline.empty')}/>
            ) : (
              <div className="gt-activity-list">
                {entries.map((entry) => {
                  const when = dayjs(entry.dateTrack, MONITORING_DATE_FORMAT);
                  const hasAttachment =
                    entry.hasAttachment || entry.indicatorAttachment === 'S';
                  return (
                    <div key={entry.uid ?? entry.id} className="gt-activity-entry">
                      <Avatar
                        size={32}
                        style={{
                          backgroundColor: tokens.colors.textQuaternary,
                          color: tokens.colors.textInverse,
                        }}
                      >
                        {initialsOf(entry.fullNameUserRegister)}
                      </Avatar>
                      <div className="gt-activity-entry-body">
                        <div className="gt-activity-entry-meta">
                          <span className="gt-activity-entry-user">
                            {entry.fullNameUserRegister}
                          </span>
                          {entry.descriptionStateIncident && (
                            <Tag
                              color={
                                entry.colorStateIncident
                                  ? `#${entry.colorStateIncident}`
                                  : undefined
                              }
                              style={{marginInlineStart: tokens.spacing.x8}}
                            >
                              {entry.descriptionStateIncident}
                            </Tag>
                          )}
                          <span className="gt-activity-entry-sep"> · </span>
                          <Tooltip title={when.isValid() ? when.format(dateTimeFormat) : entry.dateTrack}>
                            <span className="gt-activity-entry-time">
                              {when.isValid() ? when.fromNow() : entry.dateTrack}
                            </span>
                          </Tooltip>
                        </div>
                        {entry.descriptionTrack && (
                          <div className="gt-activity-entry-bubble">{entry.descriptionTrack}</div>
                        )}
                        {hasAttachment && (
                          <EntryAttachments issueUid={issueUid} entryUid={entry.uid}/>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <div className="gt-activity-side">
            <IssueSummaryCard
              code={summary.code}
              statusLabel={summary.statusLabel}
              statusColor={summary.statusColor}
              reportedBy={summary.reportedBy}
              reportedOn={summary.reportedOn}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default IssueActivity;
